/**
 * Componente atómico para el slot izquierdo del botón
 */

import React from 'react';
import { Visual } from '@/modules/Visual/Index';
import { IconFontColor, IconFontSize, ButtonColors, VisualSlotType } from '@/shared/types/types';
import { cn } from '@/shared/utils/cn';
import { getIconFontColor } from '../utils/colorUtils';

export interface ButtonSlotLeftProps {
  /** Slot izquierdo */
  leftSlot?: VisualSlotType;
  /** Tamaño del botón */
  size?: 'xxs' | 'xs' | 'sm' | 'md' | 'lg';
  /** Color del botón */
  color?: ButtonColors;
  /** Color personalizado del icono */
  colorIcon?: IconFontColor | IconFontColor;
  /** Clases CSS adicionales */
  className?: string;
  /** Si el botón está deshabilitado */
  disabled?: boolean;
  /** Ancho de línea del icono */
  strokeWidth?: number;
  /** Handler de click del slot izquierdo */
  onClickLeftSlot?: (e: React.MouseEvent<HTMLDivElement>) => void;
  /** Props de duotone para slot izquierdo */
  leftSlotDuotonePrimary?: IconFontColor;
  leftSlotDuotoneSecondary?: IconFontColor;
  leftSlotDuotoneOpacityPrimary?: number;
  leftSlotDuotoneOpacitySecondary?: number;
  /** Si hay children (texto) */
  hasChildren: boolean;
  /** Tamaño del icono del slot (prioridad sobre el derivado del tamaño del botón). */
  iconFontSize?: IconFontSize;
}

/**
 * Renderiza el slot izquierdo del botón
 */
export const ButtonSlotLeft: React.FC<ButtonSlotLeftProps> = ({
  leftSlot,
  size,
  color,
  colorIcon,
  className,
  disabled,
  strokeWidth = 2,
  onClickLeftSlot,
  leftSlotDuotonePrimary,
  leftSlotDuotoneSecondary,
  leftSlotDuotoneOpacityPrimary,
  leftSlotDuotoneOpacitySecondary,
  hasChildren,
  iconFontSize,
}) => {
  if (!hasChildren || !leftSlot) {
    return null;
  }

  const iconColor = getIconFontColor(color, colorIcon);
  const resolvedIconSize: IconFontSize = iconFontSize ?? (size === 'xxs' ? 'xs' : 'sm');

  return (
    <Visual
      slot={leftSlot}
      size={resolvedIconSize}
      colorIcon={iconColor}
      className={cn(
        'ui-shrink-0',
        className,
        color === 'primary' && disabled && '!ui-text-on-color'
      )}
      onClick={onClickLeftSlot}
      strokeWidth={strokeWidth}
      colorIconDuotonePrimary={leftSlotDuotonePrimary}
      colorIconDuotoneSecondary={leftSlotDuotoneSecondary}
      opacityIconDuotonePrimary={leftSlotDuotoneOpacityPrimary}
      opacityIconDuotoneSecondary={leftSlotDuotoneOpacitySecondary}
    />
  );
};

ButtonSlotLeft.displayName = 'ButtonSlotLeft';

/**
 * Componente atómico para el slot derecho del botón
 */

export interface ButtonSlotRightProps {
  /** Slot derecho */
  rightSlot?: VisualSlotType;
  /** Tamaño del botón */
  size?: 'xxs' | 'xs' | 'sm' | 'md' | 'lg';
  /** Color del botón */
  color?: ButtonColors;
  /** Color personalizado del icono */
  colorIcon?: IconFontColor | IconFontColor;
  /** Clases CSS adicionales */
  className?: string;
  /** Si el botón está deshabilitado */
  disabled?: boolean;
  /** Ancho de línea del icono */
  strokeWidth?: number;
  /** Handler de click del slot derecho */
  onClickRightSlot?: (e: React.MouseEvent<HTMLDivElement>) => void;
  /** Props de duotone para slot derecho */
  rightSlotDuotonePrimary?: IconFontColor;
  rightSlotDuotoneSecondary?: IconFontColor;
  rightSlotDuotoneOpacityPrimary?: number;
  rightSlotDuotoneOpacitySecondary?: number;
  /** Si hay children (texto) */
  hasChildren: boolean;
  /** Tamaño del icono del slot (prioridad sobre el derivado del tamaño del botón). */
  iconFontSize?: IconFontSize;
}

/**
 * Renderiza el slot derecho del botón
 */
export const ButtonSlotRight: React.FC<ButtonSlotRightProps> = ({
  rightSlot,
  size,
  color,
  colorIcon,
  className,
  disabled,
  strokeWidth = 2,
  onClickRightSlot,
  rightSlotDuotonePrimary,
  rightSlotDuotoneSecondary,
  rightSlotDuotoneOpacityPrimary,
  rightSlotDuotoneOpacitySecondary,
  hasChildren,
  iconFontSize,
}) => {
  if (!hasChildren || !rightSlot) {
    return null;
  }

  const iconColor = getIconFontColor(color, colorIcon);
  const resolvedIconSize: IconFontSize = iconFontSize ?? (size === 'xxs' ? 'xs' : 'sm');

  return (
    <Visual
      slot={rightSlot}
      size={resolvedIconSize}
      colorIcon={iconColor}
      className={cn(
        'ui-shrink-0',
        className,
        color === 'primary' && disabled && '!ui-text-on-color'
      )}
      onClick={onClickRightSlot}
      strokeWidth={strokeWidth}
      colorIconDuotonePrimary={rightSlotDuotonePrimary}
      colorIconDuotoneSecondary={rightSlotDuotoneSecondary}
      opacityIconDuotonePrimary={rightSlotDuotoneOpacityPrimary}
      opacityIconDuotoneSecondary={rightSlotDuotoneOpacitySecondary}
    />
  );
};

ButtonSlotRight.displayName = 'ButtonSlotRight';
